import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Users, Briefcase, MessageSquare, LogOut, 
  Brain, Settings, Bell, Search, Calendar, Clock,
  CheckCircle, FileText, Mail, Phone, Plus, ChevronRight,
  Inbox, Send, Archive, Trash, Paperclip, Image, Globe
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import PortalOverview from '../../components/dcvee/PortalOverview';
import AIMentor from '../../components/dcvee/AIMentor';

const translations = {
  es: {
    title: 'Espacio de Trabajo Virtual',
    subtitle: 'Tu entorno laboral digital',
    sections: {
      overview: 'Vista General',
      tasks: 'Tareas',
      chat: 'Chat',
      files: 'Archivos',
      browser: 'Navegador'
    },
    email: {
      compose: 'Redactar',
      inbox: 'Bandeja de entrada',
      sent: 'Enviados',
      drafts: 'Borradores',
      archive: 'Archivo',
      search: 'Buscar correos...'
    },
    calendar: {
      today: 'Hoy',
      upcoming: 'Próximos eventos',
      schedule: 'Agendar'
    },
    tasks: {
      new: 'Nueva tarea',
      due: 'Vencimiento',
      priority: 'Prioridad'
    }
  },
  en: {
    title: 'Virtual Workspace',
    subtitle: 'Your digital work environment',
    sections: {
      overview: 'Overview',
      tasks: 'Tasks',
      chat: 'Chat',
      files: 'Files',
      browser: 'Browser'
    },
    email: {
      compose: 'Compose',
      inbox: 'Inbox',
      sent: 'Sent',
      drafts: 'Drafts',
      archive: 'Archive',
      search: 'Search emails...'
    },
    calendar: {
      today: 'Today',
      upcoming: 'Upcoming events',
      schedule: 'Schedule'
    },
    tasks: {
      new: 'New task',
      due: 'Due date',
      priority: 'Priority'
    }
  }
};

const DashboardPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const t = translations[language];

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-background border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Building2 className="h-8 w-8 text-accent" />
                <span className="ml-2 text-xl font-bold text-foreground">
                  Virtual Office
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-foreground/50" />
              </div>
              <div className="flex items-center space-x-2">
                <Globe className="h-5 w-5 text-foreground/70" />
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as 'en' | 'es')}
                  className="bg-secondary/30 border border-border rounded-lg px-3 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="en">English</option>
                  <option value="es">Español</option>
                </select>
              </div>
              <button className="text-foreground/70 hover:text-foreground relative">
                <Bell className="h-5 w-5" />
                <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500"></span>
              </button>
              <button className="text-foreground/70 hover:text-foreground">
                <Settings className="h-5 w-5" />
              </button>
              <div className="border-l border-border h-6"></div>
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-accent/10 flex items-center justify-center text-accent font-medium">
                  {user?.user_metadata?.name?.[0] || 'U'}
                </div>
                <button 
                  onClick={handleSignOut}
                  className="ml-4 text-foreground/70 hover:text-foreground"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="border-b border-border mb-8">
          <nav className="-mb-px flex space-x-8">
            {Object.entries(t.sections).map(([key, value]) => (
              <button
                key={key}
                onClick={() => {
                  if (key === 'browser') {
                    navigate('/dcvee/systems/browser');
                  } else {
                    setActiveTab(key);
                  }
                }}
                className={`${
                  activeTab === key
                    ? 'border-accent text-accent'
                    : 'border-transparent text-foreground/70 hover:text-foreground hover:border-border'
                } py-4 px-1 border-b-2 font-medium text-sm`}
              >
                {value}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div>
          {activeTab === 'overview' && <PortalOverview />}
          {activeTab === 'tasks' && <AIMentor />}
          {/* Chat Section */}
          {activeTab === 'chat' && (
            <div className="dark-card p-6">
              <div className="flex flex-col h-[600px]">
                <div className="flex-1 overflow-y-auto px-4 py-2">
                  {/* Chat messages would go here */}
                </div>
                <div className="border-t border-border p-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      placeholder="Type a message..."
                      className="flex-1 px-4 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                    <button className="p-2 text-accent hover:text-accent/90">
                      <Paperclip className="h-5 w-5" />
                    </button>
                    <button className="p-2 text-accent hover:text-accent/90">
                      <Image className="h-5 w-5" />
                    </button>
                    <button className="p-2 text-accent hover:text-accent/90">
                      <Send className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Files Section */}
          {activeTab === 'files' && (
            <div className="dark-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-medium text-foreground">Files</h2>
                <button className="inline-flex items-center px-3 py-1 border border-transparent rounded-md text-sm font-medium text-white bg-accent hover:bg-accent/90">
                  <Plus className="h-4 w-4 mr-1" />
                  Upload File
                </button>
              </div>
              {/* File list would go here */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;